// src/components/SearchModal.js
import React, { useState, useEffect, useMemo } from 'react';
import axios  from 'axios';
import styles from './SearchModal.module.css';

const clean = str => (str || '')
  .toLowerCase()
  .replace(/♯/g, '#')
  .replace(/♭/g, 'b')
  .replace(/\s+/g, '');

const parseKey = str => {
  let s = clean(str);
  let quality = '';

  if (/(minor|min|mi)$/.test(s)) {
    quality = 'min';
    s = s.replace(/(minor|min|mi)$/,'');
  } else if (/(major|maj|ma)$/.test(s)) {
    quality = 'maj';
    s = s.replace(/(major|maj|ma)$/,'');
  } else if (s.endsWith('m')) {
    s = s.slice(0,-1);
  }
  return { root: s, quality };
};

const keyFullRe = /^[a-g][b#]?(?:m|min|mi|major|maj|ma|minor)?$/i;
const bpmRe     = /^\d{1,3}(?:bpm)?$/i;

const normalizeQuery = str => {
  const raw     = str.toLowerCase().trim().split(/\s+/);
  const rootRe  = /^[a-g][b#]?$/;
  const qualRe  = /^(m|min|mi|major|maj|ma|minor)$/;
  const out     = [];

  for (let i = 0; i < raw.length; i++) {
    let t = raw[i];

    if (rootRe.test(t) && i + 1 < raw.length && qualRe.test(raw[i + 1])) {
      t = t + raw[i + 1];
      i++;
    }

    if (bpmRe.test(t)) {
      out.push(t.replace('bpm',''));
      continue;
    }

    if (keyFullRe.test(t)) {
      const { root, quality } = parseKey(t);
      out.push(root);
      if (quality) out.push(quality);
      continue;
    }

    out.push(t);
  }
  return out.join(' ');
};

export default function SearchModal({ onClose, onSubmit, initialBeats }) {
  const [value, setValue] = useState('');
  const [beats, setBeats] = useState([]);
  const [loading, setLoad]  = useState(true);

  useEffect(() => {
    if (initialBeats) {
    setBeats(initialBeats);
    setLoad(false);
    return;
  }
    let cancelled = false;
    (async () => {
      const { data } = await axios.get('/api/beats');
      if (!cancelled) { setBeats(data); setLoad(false); }
    })();
    return () => { cancelled = true; };
  }, [initialBeats]);

  const matches = useMemo(() => {
    if (loading || !value.trim()) return [];

    const raw = value.toLowerCase().trim().split(/\s+/);
    const rootR = /^[a-g][b#]?$/;
    const qualR = /^(m|min|mi|major|maj|ma|minor)$/;
    const tokens=[];
    for(let i=0;i<raw.length;i++){
      const t=raw[i];
      if(rootR.test(t)&&i+1<raw.length&&qualR.test(raw[i+1])){
        tokens.push(t+raw[i+1]); i++;
      } else tokens.push(t);
    }

    return beats.filter(b =>
      tokens.every(token => {
        if (bpmRe.test(token)) {
          const num = token.replace('bpm','');
          return String(b.bpm).includes(num);
        }
        if (keyFullRe.test(token)) {
          const { root: tokRoot, quality: tokQ } = parseKey(token);
          const { root: keyRoot, quality: keyQ } = parseKey(b.keySignature || '');
          if (tokRoot !== keyRoot) return false;
          return tokQ ? tokQ === keyQ : true;
        }
        const fields = [b.title, b.genre, b.artistSticker, ...b.moodTags];
        return fields.some(f => f.toLowerCase().includes(token));
      })
    );
  }, [value, beats, loading]);

  const handleKeyDown = e => {
    if (e.key === 'Enter')  onSubmit(normalizeQuery(value));
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <input
          className={styles.input}
          placeholder="Title / Genre / Key / BPM / Mood"
          autoFocus
          value={value}
          onChange={e=>setValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {matches.length > 0 && (
          <ul className={styles.preview}>
            {matches.map(b => (
              <li key={b.id} onClick={() => onSubmit(b.title)}>
                {b.title} — {b.genre}
                {b.bpm          && <> · {b.bpm} BPM</>}
                {b.keySignature && <> · {b.keySignature}</>}
              </li>
            ))}
          </ul>
        )}	
	{matches.length === 0 && value.trim() && !loading && (
          <ul className={styles.preview}>
            <li className={styles.noMatches}>No matches</li>
          </ul>
        )}
      </div>
    </div>
  );
}

